import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  Send as SendIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  getEmailSettings,
  updateEmailSettings,
  sendTestEmail,
  getCompanySettings,
  updateCompanySettings,
  getBankSettings,
  updateBankSettings,
  EmailSettings,
  CompanySettings,
  BankSettings,
} from '../../services/api/settingsService';
import { getToken } from '@/utils/storage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SettingsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Email Settings
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password_set: false,
    from_address: '',
    from_name: '',
    use_tls: true,
  });
  const [smtpPassword, setSmtpPassword] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Company Settings
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: '',
    address: '',
    tel: '',
    email: '',
  });

  // Bank Settings
  const [bankSettings, setBankSettings] = useState<BankSettings>({
    bank_name: '',
    branch_name: '',
    account_type: '普通',
    account_number: '',
    account_holder: '',
  });

  // Password Change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [email, company, bank] = await Promise.all([
        getEmailSettings(),
        getCompanySettings(),
        getBankSettings(),
      ]);
      setEmailSettings(email);
      setCompanySettings(company);
      setBankSettings(bank);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setSnackbar({ open: true, message: '設定の読み込みに失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSaving(true);
    try {
      await updateEmailSettings({
        enabled: emailSettings.enabled,
        smtp_host: emailSettings.smtp_host || undefined,
        smtp_port: emailSettings.smtp_port,
        smtp_user: emailSettings.smtp_user || undefined,
        smtp_password: smtpPassword || undefined,
        from_address: emailSettings.from_address || undefined,
        from_name: emailSettings.from_name || undefined,
        use_tls: emailSettings.use_tls,
      });
      setSnackbar({ open: true, message: 'メール設定を保存しました', severity: 'success' });
      setSmtpPassword('');
      // Reload to get updated smtp_password_set status
      const updated = await getEmailSettings();
      setEmailSettings(updated);
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setSnackbar({ open: true, message: 'メール設定の保存に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      setSnackbar({ open: true, message: 'テスト送信先メールアドレスを入力してください', severity: 'error' });
      return;
    }
    setTestingEmail(true);
    try {
      await sendTestEmail(testEmailAddress);
      setSnackbar({ open: true, message: `テストメールを ${testEmailAddress} に送信しました`, severity: 'success' });
    } catch (error: unknown) {
      console.error('Failed to send test email:', error);
      const message = error instanceof Error ? error.message : 'テストメールの送信に失敗しました';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    setSaving(true);
    try {
      await updateCompanySettings(companySettings);
      setSnackbar({ open: true, message: '会社情報を保存しました', severity: 'success' });
    } catch (error) {
      console.error('Failed to save company settings:', error);
      setSnackbar({ open: true, message: '会社情報の保存に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankSettings = async () => {
    setSaving(true);
    try {
      await updateBankSettings(bankSettings);
      setSnackbar({ open: true, message: '銀行情報を保存しました', severity: 'success' });
    } catch (error) {
      console.error('Failed to save bank settings:', error);
      setSnackbar({ open: true, message: '銀行情報の保存に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // バリデーション
    if (newPassword.length < 8) {
      setSnackbar({ open: true, message: '新しいパスワードは8文字以上で入力してください', severity: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: '新しいパスワードと確認用パスワードが一致しません', severity: 'error' });
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'パスワード変更に失敗しました');
      }

      setSnackbar({ open: true, message: 'パスワードを変更しました', severity: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      const message = error instanceof Error ? error.message : 'パスワード変更に失敗しました';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        システム設定
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<PersonIcon />} label="アカウント" />
          <Tab icon={<EmailIcon />} label="メール設定" />
          <Tab icon={<BusinessIcon />} label="会社情報" />
          <Tab icon={<BankIcon />} label="銀行情報" />
        </Tabs>
      </Paper>

      {/* アカウント設定タブ */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              パスワード変更
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type={showCurrentPassword ? 'text' : 'password'}
                  label="現在のパスワード"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  label="新しいパスワード"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="8文字以上で入力してください"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  label="新しいパスワード（確認）"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                  helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'パスワードが一致しません' : ''}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={changingPassword ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              >
                {changingPassword ? '変更中...' : 'パスワードを変更'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* メール設定タブ */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* SMTP設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SMTP設定
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={emailSettings.enabled}
                      onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                    />
                  }
                  label="メール送信を有効にする"
                  sx={{ mb: 2 }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="SMTPホスト"
                      value={emailSettings.smtp_host || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="ポート"
                      value={emailSettings.smtp_port}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTPユーザー"
                      value={emailSettings.smtp_user || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })}
                      placeholder="your-email@gmail.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      label="SMTPパスワード"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder={emailSettings.smtp_password_set ? '（設定済み - 変更する場合は入力）' : 'パスワードを入力'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="送信元メールアドレス"
                      value={emailSettings.from_address || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_address: e.target.value })}
                      placeholder="noreply@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="送信者名"
                      value={emailSettings.from_name || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                      placeholder="システム通知"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailSettings.use_tls}
                          onChange={(e) => setEmailSettings({ ...emailSettings, use_tls: e.target.checked })}
                        />
                      }
                      label="TLSを使用する（推奨）"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEmailSettings}
                    disabled={saving}
                  >
                    {saving ? '保存中...' : '設定を保存'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* テスト送信 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  テスト送信
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {!emailSettings.enabled && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    メール送信が無効になっています。テスト送信するには、上記で設定を有効にして保存してください。
                  </Alert>
                )}

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="テスト送信先メールアドレス"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="test@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={testingEmail ? <CircularProgress size={20} /> : <SendIcon />}
                      onClick={handleTestEmail}
                      disabled={testingEmail || !emailSettings.enabled}
                    >
                      {testingEmail ? '送信中...' : 'テスト送信'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 会社情報タブ */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              会社情報（請求書に表示）
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="会社名"
                  value={companySettings.name || ''}
                  onChange={(e) => setCompanySettings({ ...companySettings, name: e.target.value })}
                  placeholder="株式会社〇〇"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="住所"
                  value={companySettings.address || ''}
                  onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                  placeholder="東京都渋谷区..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="電話番号"
                  value={companySettings.tel || ''}
                  onChange={(e) => setCompanySettings({ ...companySettings, tel: e.target.value })}
                  placeholder="03-xxxx-xxxx"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="メールアドレス"
                  value={companySettings.email || ''}
                  onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                  placeholder="info@example.com"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCompanySettings}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* 銀行情報タブ */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              銀行情報（請求書の振込先に表示）
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="銀行名"
                  value={bankSettings.bank_name || ''}
                  onChange={(e) => setBankSettings({ ...bankSettings, bank_name: e.target.value })}
                  placeholder="〇〇銀行"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="支店名"
                  value={bankSettings.branch_name || ''}
                  onChange={(e) => setBankSettings({ ...bankSettings, branch_name: e.target.value })}
                  placeholder="〇〇支店"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="口座種別"
                  value={bankSettings.account_type || ''}
                  onChange={(e) => setBankSettings({ ...bankSettings, account_type: e.target.value })}
                  placeholder="普通"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="口座番号"
                  value={bankSettings.account_number || ''}
                  onChange={(e) => setBankSettings({ ...bankSettings, account_number: e.target.value })}
                  placeholder="1234567"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="口座名義"
                  value={bankSettings.account_holder || ''}
                  onChange={(e) => setBankSettings({ ...bankSettings, account_holder: e.target.value })}
                  placeholder="カ）〇〇"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveBankSettings}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
